import { useState, useEffect, useId, useRef, useCallback, useMemo } from "react"
import {
  Loader2Icon,
  RefreshCw,
  BellRingIcon,
  CheckCircle2Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "lucide-react"
import {
  Link,
  useParams,
  useSearchParams,
  useNavigate,
  useBlocker,
} from "react-router"
import { useTranslation } from "react-i18next"

import { CopyButton } from "@f2e/ui/components/Button"
import type { ConversationPartApiResponse } from "@f2e/ui/chat-v2"
import { useStore } from "@/store"
import {
  useCreateAgentApi,
  useUpdateAgentApi,
  useGetAgentsApi,
  agentResponseToAgent,
  makeCreateAgentRequest,
  makeUpdateAgentRequest,
  FetchStatus,
} from "@/hooks/api"
import { useWindowSize, useToast, useBeforeUnload } from "@/hooks"
import {
  Button,
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbSeparator,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@f2e/ui/components/base"

import { AlertBox, TipIconButton } from "@/components"
import { ErrorPage, LoadingPage } from "@/components/System"
import { Agent, AgentType, EvaluationCaseLabel, Workspace } from "@/types"
import { useAgentHistories } from "@/hooks/useAgentHistories"
import useConversationEval from "@/hooks/useConversationEval"
import { CaseDetailModal } from "@/components/Evaluation"
import {
  evaluationCaseLabelResponseToData,
  useListEvaluationCaseLabelsApi,
} from "@/hooks/api/eval"

import {
  ConfigPanel,
  LockPanel,
  ThreadPanel,
  ChatContextProvider,
  type ThreadPanelHandle,
} from "./components/Playground"
import AgentTestHistorySelector from "./components/Playground/AgentTestHistorySelector"
import AgentBreadcrumbSelector from "./components/AgentBreadcrumbSelector"
import PageBreadcrumbSelector from "./components/PageBreadcrumbSelector"
import AgentHistoryPublishDialog from "./components/Agent/AgentHistoryPublishDialog"
import { AgentPublicationStatus } from "./components/Agent/AgentPublicationStatus"
import { VersionNoteDialog } from "./components/Agent/VersionNoteDialog"
import { BasicFormType } from "./components/Agent/type"
import { CopilotHiddenField } from "@/components/Copilot/CopilotHiddenField"
import EvaluationTablePage from "./EvaluationTablePage"
import {
  completeCopilotIntroduction,
  getAgentPlaygroundContentState,
  hasSeenCopilotIntroduction,
  isVersionTestGuidanceCurrent,
  markCopilotIntroductionSeen,
  startVersionTest,
} from "./agentPlaygroundState"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"
import { useAgentTestHistories } from "@/hooks/useAgentTestHistories"
import {
  useAgentPublicationState,
  isPublicationActionDisabled,
  type AgentPublicationResult,
} from "./useAgentPublicationState"
import {
  isSuggestionApplyEventForCurrentPath,
  isSuccessfulApplyAllEvent,
  subscribeSuggestionApplyEvents,
} from "@/components/Copilot/applySuggestion/applySuggestionHandler"

const PANEL_TOGGLE_DELAY_MS = 300
const REFRESH_ANIMATION_DURATION_MS = 500
type NewAgentOnboarding = {
  agentId: string
  stage: "live-test" | "copilot"
}

type CopilotDraftState = {
  agentId: string
  appliedKeys: string[]
}

type VersionTestGuidance = {
  agentId: string
  question?: string
  version?: number
}

const AgentPlaygroundPage = () => {
  const { t } = useTranslation()
  const { isMobile } = useWindowSize()
  const { workspaceId, agentId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [
    currentOrganization,
    setIsCopilotOpen,
    setCopilotMode,
    requestCopilotNewChat,
  ] = useStore((state) => [
    state.currentOrganization,
    state.setIsCopilotOpen,
    state.setCopilotMode,
    state.requestCopilotNewChat,
  ])

  const [isLeftPanelOpen, setIsLeftPanelOpen] = useState(true)
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(true)
  const [workspace, setWorkspace] = useState<Workspace>()
  const [labels, setLabels] = useState<EvaluationCaseLabel[]>([])
  const [isAgentProcessing, setIsAgentProcessing] = useState(false)
  const [agents, setAgents] = useState<Agent[]>([])
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false)
  const [pendingAgentSelection, setPendingAgentSelection] =
    useState<Agent | null>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [showCenterToggle, setShowCenterToggle] = useState(true)
  const [newAgentOnboarding, setNewAgentOnboarding] =
    useState<NewAgentOnboarding | null>(null)
  const [copilotDraftState, setCopilotDraftState] =
    useState<CopilotDraftState | null>(null)
  const [versionTestGuidance, setVersionTestGuidance] =
    useState<VersionTestGuidance | null>(null)
  const [lastLiveTestQuestion, setLastLiveTestQuestion] = useState<
    string | undefined
  >()
  const prevPanelStateRef = useRef<{
    isLeftPanelOpen: boolean
    isRightPanelOpen: boolean
  } | null>(null)
  const prevIsNewAgentModeRef = useRef(false)
  const prevIsNewAgentModeForCopilotRef = useRef(false)

  // Warn user when leaving page with unsaved changes (browser reload/close/back)
  useBeforeUnload(hasUnsavedChanges)

  // Block navigation within the app when there are unsaved changes
  const blocker = useBlocker(hasUnsavedChanges)

  // Handle navigation blocker - show AlertBox instead of window.confirm
  useEffect(() => {
    if (blocker.state === "blocked") {
      setShowUnsavedChangesDialog(true)
    }
  }, [blocker.state])

  // Eval modal hook - includes evaluators
  const {
    isEvalModalOpen,
    evalCaseData,
    evaluators,
    handleCreateEvalFromParts,
    handleCloseEvalModal,
  } = useConversationEval(workspaceId)
  const { data: labelsResp, fetchFn: fetchLabels } =
    useListEvaluationCaseLabelsApi(workspaceId)

  useEffect(() => {
    if (isEvalModalOpen && workspaceId) {
      fetchLabels()
    }
  }, [fetchLabels, isEvalModalOpen, workspaceId])

  const isNewAgentMode = agentId === "new"
  const currentPage =
    searchParams.get("tab") === "evaluation" ? "evaluation" : "playground"
  const isEvaluationPage = currentPage === "evaluation"
  const buildAgentPath = useCallback(
    (nextAgentId: string | number) => {
      const path = `/workspaces/${workspaceId}/agents/${nextAgentId}`
      return isEvaluationPage ? `${path}?tab=evaluation` : path
    },
    [isEvaluationPage, workspaceId]
  )

  // Derived state from agents array and agentId
  const currentAgent = useMemo(() => {
    if (isNewAgentMode || !agentId) return undefined
    return agents.find((agent) => agent.id.toString() === agentId)
  }, [agents, agentId, isNewAgentMode])

  useEffect(
    () =>
      subscribeSuggestionApplyEvents((event) => {
        if (!isSuccessfulApplyAllEvent(event) || !currentAgent) return
        if (!isSuggestionApplyEventForCurrentPath(event)) return
        const appliedKeys = event.appliedKeys.filter((key) =>
          key.startsWith("basicForm.")
        )
        if (appliedKeys.length === 0) return

        setCopilotDraftState({
          agentId: currentAgent.id.toString(),
          appliedKeys,
        })
        setVersionTestGuidance(null)
        setNewAgentOnboarding(null)
        setIsCopilotOpen(false)
        setIsLeftPanelOpen(true)
        setIsRightPanelOpen(true)
      }),
    [currentAgent, setIsCopilotOpen]
  )

  useEffect(() => {
    if (labelsResp?.data) {
      setLabels(labelsResp.data.map(evaluationCaseLabelResponseToData))
    }
  }, [labelsResp])

  // Move agent histories state to main component level so both panels can access it
  const historiesState = useAgentHistories(currentAgent?.id?.toString() || "")
  const publicationState = useAgentPublicationState(currentAgent)

  // Derive agent type from current agent or search params for new agent mode
  const agentType = useMemo(() => {
    if (isNewAgentMode) {
      return searchParams.get("type") as AgentType
    }
    return currentAgent?.type
  }, [isNewAgentMode, searchParams, currentAgent])

  // APIs
  const {
    fetchFn: getAgents,
    data: agentsResp,
    status: agentsStatus,
  } = useGetAgentsApi()

  // close right panel on mobile
  useEffect(() => {
    if (isMobile) {
      setIsRightPanelOpen(false)
    }
  }, [isMobile])

  useEffect(() => {
    const wasNewAgentMode = prevIsNewAgentModeRef.current

    if (isNewAgentMode && !wasNewAgentMode) {
      prevPanelStateRef.current = {
        isLeftPanelOpen,
        isRightPanelOpen,
      }
      setIsLeftPanelOpen(true)
      setIsRightPanelOpen(false)
    }

    if (!isNewAgentMode && wasNewAgentMode && prevPanelStateRef.current) {
      const isStartingFirstLiveTest =
        newAgentOnboarding?.agentId === agentId &&
        newAgentOnboarding?.stage === "live-test"
      setIsLeftPanelOpen(
        isStartingFirstLiveTest
          ? !isMobile
          : prevPanelStateRef.current.isLeftPanelOpen
      )
      setIsRightPanelOpen(
        isStartingFirstLiveTest
          ? true
          : isMobile
            ? false
            : prevPanelStateRef.current.isRightPanelOpen
      )
      prevPanelStateRef.current = null
    }

    prevIsNewAgentModeRef.current = isNewAgentMode
  }, [
    agentId,
    isLeftPanelOpen,
    isMobile,
    isNewAgentMode,
    isRightPanelOpen,
    newAgentOnboarding,
  ])

  useEffect(() => {
    const wasNewAgentMode = prevIsNewAgentModeForCopilotRef.current

    if (isNewAgentMode && !wasNewAgentMode) {
      // Start with the Template unobstructed. Closing Copilot preserves its
      // always-available bottom-right launcher.
      setIsCopilotOpen(false)
    }

    prevIsNewAgentModeForCopilotRef.current = isNewAgentMode
  }, [isNewAgentMode, setIsCopilotOpen])

  // Check if workspace belongs to current organization
  // Workspaces are already filtered by organization, so if workspace doesn't exist
  // in the current workspaces list, it means it belongs to a different organization
  useEffect(() => {
    if (!workspaceId || !currentOrganization) return

    const state = useStore.getState()
    const workspace = state.workspaces.find((w) => w.id === workspaceId)

    if (!workspace) {
      console.log(
        "Workspace not found in current organization, redirecting to home"
      )
      navigate("/")
      return
    }
  }, [workspaceId, currentOrganization, navigate])

  // existing workspace
  useEffect(() => {
    if (!workspaceId) return

    const state = useStore.getState()
    const workspace = state.workspaces.find((w) => w.id === workspaceId)
    if (!workspace) return

    const userMe = state.userMe
    if (!userMe?.profile?.defaultOrganizationId) return

    setWorkspace(workspace)

    // Fetch agents for this workspace
    getAgents({
      searchParams: new URLSearchParams({
        wid: workspace.id,
        oid: userMe.profile.defaultOrganizationId,
      }),
    })
  }, [workspaceId, getAgents])

  // Handle agents response
  useEffect(() => {
    if (!agentsResp || !agentsResp.data) return
    const fetchedAgents = agentsResp.data.map(agentResponseToAgent)
    setAgents(fetchedAgents)
  }, [agentsResp])

  const toggleLeftPanel = () => {
    // Ensure at least one panel stays visible by opening the opposite side first.
    if (isLeftPanelOpen) {
      if (!isRightPanelOpen) {
        setIsRightPanelOpen(true)
      }
      setIsLeftPanelOpen(false)
      return
    }
    setIsLeftPanelOpen(true)
  }

  useEffect(() => {
    if (isLeftPanelOpen && isRightPanelOpen) {
      const timer = window.setTimeout(
        () => setShowCenterToggle(true),
        PANEL_TOGGLE_DELAY_MS
      )
      return () => window.clearTimeout(timer)
    }
    setShowCenterToggle(false)
  }, [isLeftPanelOpen, isRightPanelOpen])

  const toggleRightPanel = () => {
    // Ensure at least one panel stays visible by opening the opposite side first.
    if (isRightPanelOpen) {
      if (!isLeftPanelOpen) {
        setIsLeftPanelOpen(true)
      }
      setIsRightPanelOpen(false)
      return
    }
    setIsRightPanelOpen(true)
  }

  const handleAgentChanged = useCallback(
    (changedAgent: Agent, isCreate: boolean) => {
      setHasUnsavedChanges(false)
      if (isNewAgentMode) {
        // Add new agent to the array
        setAgents((prev) => [...prev, changedAgent])
        setNewAgentOnboarding({
          agentId: changedAgent.id.toString(),
          stage: "live-test",
        })
        setVersionTestGuidance(null)
        setIsCopilotOpen(false)
        // Navigate to the new agent
        navigate(buildAgentPath(changedAgent.id))
      } else {
        // Update existing agent in the array
        setAgents((prev) =>
          prev.map((agent) =>
            agent.id === changedAgent.id ? changedAgent : agent
          )
        )

        if (
          !isCreate &&
          copilotDraftState?.agentId === changedAgent.id.toString()
        ) {
          setCopilotDraftState(null)
          setVersionTestGuidance({
            agentId: changedAgent.id.toString(),
            question: lastLiveTestQuestion,
            version: changedAgent.latestVersionNumber,
          })
          setIsCopilotOpen(false)
          setIsLeftPanelOpen(true)
          setIsRightPanelOpen(true)
        }
      }
    },
    [
      buildAgentPath,
      copilotDraftState,
      isNewAgentMode,
      lastLiveTestQuestion,
      navigate,
      setIsCopilotOpen,
    ]
  )

  const activeNewAgentOnboarding =
    newAgentOnboarding?.agentId === currentAgent?.id.toString()
      ? newAgentOnboarding
      : null
  const activeVersionTestGuidance = isVersionTestGuidanceCurrent({
    guidance: versionTestGuidance,
    currentAgentId: currentAgent?.id.toString(),
    currentVersion: historiesState.currentHistory?.versionNumber,
  })
    ? versionTestGuidance
    : null

  const handleFirstLiveTestCompleted = useCallback(() => {
    if (activeNewAgentOnboarding?.stage !== "live-test") return
    if (hasSeenCopilotIntroduction()) {
      setNewAgentOnboarding(null)
      return
    }

    setNewAgentOnboarding({
      ...activeNewAgentOnboarding,
      stage: "copilot",
    })
  }, [activeNewAgentOnboarding])

  const handleOpenCopilotIntroduction = useCallback(() => {
    completeCopilotIntroduction({
      action: "open",
      markSeen: markCopilotIntroductionSeen,
      clearIntroduction: () => setNewAgentOnboarding(null),
      openCopilot: () => {
        setIsRightPanelOpen(true)
        if (!isMobile) setIsLeftPanelOpen(false)
        setCopilotMode("drawer")
        requestCopilotNewChat()
        setIsCopilotOpen(true)
      },
    })
  }, [isMobile, requestCopilotNewChat, setCopilotMode, setIsCopilotOpen])

  const handleDismissCopilotIntroduction = useCallback(() => {
    completeCopilotIntroduction({
      action: "dismiss",
      markSeen: markCopilotIntroductionSeen,
      clearIntroduction: () => setNewAgentOnboarding(null),
    })
  }, [])

  const handleAgentProcessingChange = useCallback((processing: boolean) => {
    setIsAgentProcessing(processing)
  }, [])

  const handleUnsavedChangesUpdate = useCallback((hasChanges: boolean) => {
    setHasUnsavedChanges(hasChanges)
  }, [])

  const handleAgentSelect = useCallback(
    (selectedAgent: Agent, hasUnsavedChanges: boolean) => {
      if (hasUnsavedChanges) {
        setPendingAgentSelection(selectedAgent)
        setShowUnsavedChangesDialog(true)
      } else {
        // Navigate to the selected agent
        navigate(buildAgentPath(selectedAgent.id))
      }
    },
    [buildAgentPath, navigate]
  )

  const handleUnsavedChangesCancel = () => {
    // Reset blocker if it was triggered by route navigation
    if (blocker.state === "blocked") {
      blocker.reset()
    }
    setShowUnsavedChangesDialog(false)
    setPendingAgentSelection(null)
  }

  const handleCreateNewAgent = useCallback(
    (agentType: AgentType, hasUnsavedChanges: boolean) => {
      if (hasUnsavedChanges) {
        // Store the navigation action for after confirmation
        setPendingAgentSelection({
          id: `new-${agentType}`,
          type: agentType,
        } as Agent)
        setShowUnsavedChangesDialog(true)
      } else {
        // Navigate directly to new agent creation
        navigate(`/workspaces/${workspaceId}/agents/new?type=${agentType}`)
      }
    },
    [navigate, workspaceId]
  )

  const handleUnsavedChangesConfirm = () => {
    // Handle blocker case (route navigation via breadcrumbs, browser back, etc.)
    if (blocker.state === "blocked") {
      setHasUnsavedChanges(false)
      blocker.proceed()
      setShowUnsavedChangesDialog(false)
      return
    }

    // Handle pending agent selection (sidebar navigation)
    if (pendingAgentSelection) {
      setHasUnsavedChanges(false)

      // Check if this is a create new agent action
      if (pendingAgentSelection.id.startsWith("new-")) {
        const agentType = pendingAgentSelection.id.replace("new-", "")
        navigate(`/workspaces/${workspaceId}/agents/new?type=${agentType}`)
      } else {
        // Regular agent selection
        navigate(buildAgentPath(pendingAgentSelection.id))
      }
    }
    setShowUnsavedChangesDialog(false)
    setPendingAgentSelection(null)
  }

  const contentState = getAgentPlaygroundContentState({
    isEvaluationPage,
    hasCurrentAgent: Boolean(currentAgent),
    isAgentsLoadError: agentsStatus === FetchStatus.Error,
    isAgentsLoaded: agentsStatus === FetchStatus.Success,
    canRenderPlayground: Boolean(agentType && workspace),
  })

  return (
    <div
      className={
        isEvaluationPage
          ? "w-full min-w-0 mb-10"
          : "w-full h-[calc(100vh-5rem)] flex flex-col min-h-0"
      }
    >
      {/* Toolbar */}
      <div className="flex flex-col items-start gap-3 mb-5">
        <div className="flex justify-between items-center w-full">
          <Breadcrumb className="mt-4 mb-2">
            <BreadcrumbList className="text-lg">
              <BreadcrumbItem>
                <Link to="/workspaces">{t("workspace.detail.breadcrumb")}</Link>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <Link to={`/workspaces/${workspaceId}/agents`}>
                  {t("workspace.detail.tabs.agents")}
                </Link>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <AgentBreadcrumbSelector
                  agents={agents}
                  currentAgent={currentAgent}
                  isCreating={isNewAgentMode}
                  onAgentSelect={handleAgentSelect}
                  hasUnsavedChanges={hasUnsavedChanges}
                  onCreateNewAgent={handleCreateNewAgent}
                />
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {!isEvaluationPage && (
            <>
              {/* Snapshot helper (hidden): panel states */}
              <CopilotHiddenField
                fieldKey="toolbar.leftCollapseButton"
                value={isLeftPanelOpen ? "open" : "collapsed"}
              />
              <CopilotHiddenField
                fieldKey="toolbar.collapseButton"
                value={isRightPanelOpen ? "open" : "collapsed"}
              />
            </>
          )}
        </div>

        {currentAgent && (
          <PageBreadcrumbSelector
            workspaceId={workspaceId!}
            agentId={currentAgent.id.toString()}
            currentPage={currentPage}
          />
        )}
      </div>

      {/* Main content */}
      {contentState === "evaluation" && currentAgent ? (
        <EvaluationTablePage
          showNavigation={false}
          agents={agents}
          currentAgent={currentAgent}
        />
      ) : contentState === "agents-load-error" ? (
        <ErrorPage
          title={t("workspace.agent.message.getFailed.title")}
          message={t("workspace.agentPlayground.loadAgentFailed")}
          redirectTo={`/workspaces/${workspaceId}?tab=edit-agents`}
          redirectLabel={t("workspace.detail.tabs.agents")}
          redirectReload={false}
          topOffset="80px"
        />
      ) : contentState === "agent-not-found" ? (
        <ErrorPage
          title={t("workspace.agentPlayground.notFound.title")}
          message={t("workspace.agentPlayground.notFound.message")}
          redirectTo={`/workspaces/${workspaceId}?tab=edit-agents`}
          redirectLabel={t("workspace.detail.tabs.agents")}
          redirectReload={false}
          topOffset="80px"
        />
      ) : contentState === "playground" && agentType && workspace ? (
        <div
          className={`flex flex-col ${isRightPanelOpen ? "md:flex-row" : ""} w-full flex-1 relative min-h-0`}
        >
          {/* Left panel */}
          <LeftPanel
            isLeftPanelOpen={isLeftPanelOpen}
            isRightPanelOpen={isRightPanelOpen}
            agentType={agentType}
            agent={currentAgent}
            workspace={workspace}
            agents={agents}
            historiesState={historiesState}
            onAgentChanged={handleAgentChanged}
            publishedVersionNumber={publicationState.publishedVersionNumber}
            hasPublishedVersion={publicationState.hasPublishedVersion}
            onPublicationChanged={publicationState.recordPublication}
            onProcessingChange={handleAgentProcessingChange}
            onUnsavedChangesUpdate={handleUnsavedChangesUpdate}
            copilotDraftApplied={Boolean(
              currentAgent &&
              copilotDraftState?.agentId === currentAgent.id.toString()
            )}
          />

          {/* Right panel - collapsible */}
          <RightPanel
            isLeftPanelOpen={isLeftPanelOpen}
            isRightPanelOpen={isRightPanelOpen}
            agent={currentAgent}
            workspace={workspace}
            isAgentProcessing={isAgentProcessing}
            agentHistoryId={historiesState.currentHistory?.id}
            historiesState={historiesState}
            showFirstTestGuidance={
              activeNewAgentOnboarding?.stage === "live-test"
            }
            showCopilotIntroduction={
              activeNewAgentOnboarding?.stage === "copilot"
            }
            versionTestGuidance={activeVersionTestGuidance}
            onProcessingChange={handleAgentProcessingChange}
            onFirstSuccessfulResponse={handleFirstLiveTestCompleted}
            onOpenCopilot={handleOpenCopilotIntroduction}
            onDismissCopilotIntroduction={handleDismissCopilotIntroduction}
            onVersionTestStarted={() => setVersionTestGuidance(null)}
            onLatestUserQuestionChange={setLastLiveTestQuestion}
            onCreateEvalPart={handleCreateEvalFromParts}
          />
          <PanelDividerToggle
            isLeftPanelOpen={isLeftPanelOpen}
            isRightPanelOpen={isRightPanelOpen}
            showCenterToggle={showCenterToggle}
            onToggleLeftPanel={toggleLeftPanel}
            onToggleRightPanel={toggleRightPanel}
          />
        </div>
      ) : (
        <LoadingPage />
      )}

      {/* Unsaved Changes Dialog */}
      <AlertBox
        open={showUnsavedChangesDialog}
        onOpenChange={(open) => {
          if (!open) {
            // Dialog is being closed (via Escape or clicking outside)
            // Reset blocker if it was triggered by route navigation
            if (blocker.state === "blocked") {
              blocker.reset()
            }
            setPendingAgentSelection(null)
          }
          setShowUnsavedChangesDialog(open)
        }}
        title={t("workspace.agentPlayground.unsavedChanges.title")}
        description={t("workspace.agentPlayground.unsavedChanges.description")}
        confirmText={t("workspace.agentPlayground.unsavedChanges.confirm")}
        cancelText={t("workspace.agentPlayground.unsavedChanges.cancel")}
        onConfirm={handleUnsavedChangesConfirm}
        onCancel={handleUnsavedChangesCancel}
      />

      {/* Evaluation Modal */}
      {evalCaseData && (
        <CaseDetailModal
          isOpen={isEvalModalOpen}
          onClose={handleCloseEvalModal}
          caseData={evalCaseData}
          evaluators={evaluators}
          initialEvaluator={evaluators.length > 0 ? evaluators[0] : undefined}
          workspaceId={workspaceId}
          evaluationResult={undefined}
          onFieldChange={undefined}
          onRunSingleCase={undefined}
          selectedAgentHistory={null}
          isCreateMode={true}
          labels={labels}
        />
      )}
    </div>
  )
}

interface LeftPanelProps {
  publishedVersionNumber?: number
  hasPublishedVersion: boolean
  onPublicationChanged: (result: AgentPublicationResult) => void
  isLeftPanelOpen: boolean
  isRightPanelOpen: boolean
  agentType: AgentType
  agent?: Agent
  workspace: Workspace
  agents: Agent[]
  historiesState: ReturnType<typeof useAgentHistories>
  onAgentChanged?: (agent: Agent, isCreate: boolean) => void
  onProcessingChange?: (processing: boolean) => void
  onUnsavedChangesUpdate?: (hasChanges: boolean) => void
  copilotDraftApplied?: boolean
}

const LeftPanel = ({
  publishedVersionNumber,
  hasPublishedVersion,
  onPublicationChanged,
  isLeftPanelOpen,
  isRightPanelOpen,
  agentType,
  agent,
  workspace,
  agents,
  historiesState,
  onAgentChanged,
  onProcessingChange,
  onUnsavedChangesUpdate,
  copilotDraftApplied = false,
}: LeftPanelProps) => {
  const { t } = useTranslation()
  const { alertToast, successToast } = useToast()

  const formId = useId()
  const [isProcessing, setIsProcessing] = useState(false)
  const prevIsProcessingRef = useRef(isProcessing)
  const [showChanged, setShowChanged] = useState(false)
  const [isTemplatePanelOpen, setIsTemplatePanelOpen] = useState(!agent)
  const currentHistory = historiesState.currentHistory
  const historyUnavailable =
    historiesState.isLoadingInitial ||
    historiesState.isLoadingCurrentHistory ||
    !!historiesState.currentHistoryDetailError ||
    !currentHistory
  const saveLabel = !agent ? t("workspace.create") : t("workspace.apply")
  const publishLabel = currentHistory?.wasPublished
    ? t("workspace.agent.publishDialog.labels.unpublish")
    : t("workspace.agent.publishDialog.labels.publish")
  // Track displayed errors to prevent duplicate toasts
  const displayedHistoriesErrorRef = useRef<unknown>(null)
  const displayedHistoryDetailErrorRef = useRef<unknown>(null)
  const [publishDialogOpen, setPublishDialogOpen] = useState(false)
  const [showVersionNoteDialog, setShowVersionNoteDialog] = useState(false)
  const [pendingFormData, setPendingFormData] = useState<{
    agentType: AgentType
    formData: BasicFormType
  } | null>(null)
  const leftPanelWidthClass = isLeftPanelOpen
    ? isRightPanelOpen
      ? "md:w-1/2"
      : "md:w-full"
    : "md:w-0"
  const leftPanelBaseWidthClass = isLeftPanelOpen ? "w-full" : "w-0"
  const leftPanelPaddingClass = isLeftPanelOpen ? "px-1" : "px-0"
  const leftPanelVisibilityClass = isLeftPanelOpen
    ? "translate-x-0 opacity-100"
    : "-translate-x-full opacity-0 pointer-events-none"

  // APIs
  const {
    error: createBasicAgentError,
    data: createBasicAgentRespData,
    fetchFn: createBasicAgent,
  } = useCreateAgentApi()
  const {
    error: updateBasicAgentError,
    data: updateBasicAgentRespData,
    fetchFn: updateBasicAgent,
  } = useUpdateAgentApi(agent?.id.toString() || "")

  const getTitle = useCallback(() => {
    const title =
      agent?.name || t("workspace.agentPlayground.configPanel.create")
    return showChanged ? `${title} *` : title
  }, [agent, showChanged, t])

  // error handling for creating basic agent
  useEffect(() => {
    if (!createBasicAgentError) return
    console.error("Failed to create an agent", createBasicAgentError)
    // Reset processing state and restore unsaved changes indicator on error
    setIsProcessing(false)
    setShowChanged(true)
    onUnsavedChangesUpdate?.(true)
    alertToast({
      title: t("workspace.agent.message.createFailed.title"),
      description: getUserFacingApiErrorMessage(
        createBasicAgentError,
        t,
        createBasicAgentRespData?.message ||
          t("workspace.agent.message.createFailed.title")
      ),
    })
  }, [
    createBasicAgentError,
    createBasicAgentRespData,
    alertToast,
    t,
    onUnsavedChangesUpdate,
  ])

  // error handling for updating basic agent
  useEffect(() => {
    if (!updateBasicAgentError) return
    console.error("Failed to update agent", updateBasicAgentError)
    // Reset processing state and restore unsaved changes indicator on error
    // Keep pendingFormData so user can retry without re-entering version note
    setIsProcessing(false)
    setShowChanged(true)
    onUnsavedChangesUpdate?.(true)
    alertToast({
      title: t("workspace.message.updateAgentFailed"),
      description: getUserFacingApiErrorMessage(
        updateBasicAgentError,
        t,
        `${updateBasicAgentRespData?.message || t("workspace.message.updateAgentFailed")}`
      ),
    })
  }, [
    updateBasicAgentError,
    updateBasicAgentRespData,
    alertToast,
    t,
    onUnsavedChangesUpdate,
  ])

  // error handling for agent histories API
  useEffect(() => {
    const error = historiesState?.historiesError
    // Only show toast for new errors to prevent duplicates
    if (error && error !== displayedHistoriesErrorRef.current) {
      displayedHistoriesErrorRef.current = error
      console.error("Failed to load agent histories", error)
      alertToast({
        title: t("workspace.agent.history.errors.loadHistories.title"),
        description: getUserFacingApiErrorMessage(
          error,
          t,
          t("workspace.agent.history.errors.loadHistories.description")
        ),
      })
    } else if (!error) {
      // Reset when error is cleared (successful retry)
      displayedHistoriesErrorRef.current = null
    }
  }, [historiesState?.historiesError, alertToast, t])

  // error handling for current history detail API
  useEffect(() => {
    const error = historiesState?.currentHistoryDetailError
    // Only show toast for new errors to prevent duplicates
    if (error && error !== displayedHistoryDetailErrorRef.current) {
      displayedHistoryDetailErrorRef.current = error
      console.error("Failed to load current history detail", error)
      alertToast({
        title: t("workspace.agent.history.errors.loadCurrentHistory.title"),
        description: getUserFacingApiErrorMessage(
          error,
          t,
          t("workspace.agent.history.errors.loadCurrentHistory.description")
        ),
      })
    } else if (!error) {
      // Reset when error is cleared (successful retry)
      displayedHistoryDetailErrorRef.current = null
    }
  }, [historiesState?.currentHistoryDetailError, alertToast, t])

  // notify processing change
  useEffect(() => {
    // Only call if the value actually changed
    if (prevIsProcessingRef.current !== isProcessing) {
      onProcessingChange?.(isProcessing)
      prevIsProcessingRef.current = isProcessing
    }
  }, [isProcessing, onProcessingChange])

  // Reset showChanged when agent changes
  useEffect(() => {
    setShowChanged(false)
    setIsTemplatePanelOpen(!agent)
    onUnsavedChangesUpdate?.(false)
  }, [agent, onUnsavedChangesUpdate])

  const handleDirtyChange = useCallback(
    (dirty: boolean) => {
      setShowChanged(dirty)
      onUnsavedChangesUpdate?.(dirty)
    },
    [onUnsavedChangesUpdate]
  )

  const handleApply = (agentType: AgentType, formData: BasicFormType) => {
    if (!agent) {
      // For new agent creation, no version note dialog needed
      // Clear unsaved changes indicator immediately for create (no dialog)
      setShowChanged(false)
      onUnsavedChangesUpdate?.(false)
      handleCreateAgent(agentType, formData)
    } else {
      // For updates, show version note dialog
      // Don't clear unsaved changes until API succeeds
      setPendingFormData({ agentType, formData })
      setShowVersionNoteDialog(true)
    }
  }

  const handleVersionNoteConfirm = (versionNote: string) => {
    if (!pendingFormData) return
    setShowVersionNoteDialog(false)
    handleUpdateAgent(
      pendingFormData.agentType,
      pendingFormData.formData,
      versionNote
    )
    // Don't clear pendingFormData here - it will be cleared on success in onAgentSaved
    // This allows retry if API fails
  }

  const handleVersionNoteCancel = () => {
    setShowVersionNoteDialog(false)
    setPendingFormData(null)
    // Unsaved changes indicator is still shown since we didn't clear it before dialog
  }

  const handleCreateAgent = (agentType: AgentType, formData: BasicFormType) => {
    setIsProcessing(true)
    if (agentType === AgentType.Basic) {
      const basicFormData = formData as BasicFormType
      const req = makeCreateAgentRequest(
        basicFormData.name,
        basicFormData.description,
        workspace.id,
        basicFormData.systemPrompt,
        basicFormData.unifiedTools,
        basicFormData.attachments,
        basicFormData.llmModel,
        basicFormData.llmModelSettings,
        basicFormData.suggestedQuestions,
        basicFormData.humanHandoff,
        basicFormData.outputPresentationPolicy,
        basicFormData.pendingKnowledgeBindings,
        basicFormData.responseMode,
        basicFormData.aiTakeoverGreeting
      )
      createBasicAgent({
        jsonBody: req,
        callbackFn: (resp) => {
          if (!resp || !resp.data) {
            // Handle empty/malformed response - this is an unexpected state
            console.error("Create agent API returned empty or invalid response")
            setIsProcessing(false)
            setShowChanged(true)
            onUnsavedChangesUpdate?.(true)
            alertToast({
              title: t("workspace.agent.message.createFailed.title"),
              description: t(
                "workspace.message.unexpectedError",
                "An unexpected error occurred"
              ),
            })
            return
          }
          try {
            const agent = agentResponseToAgent(resp.data)
            onAgentSaved(agent, true)
          } catch (error) {
            console.error("Failed to parse create agent response", error)
            setIsProcessing(false)
            setShowChanged(true)
            onUnsavedChangesUpdate?.(true)
            alertToast({
              title: t("workspace.agent.message.createFailed.title"),
              description: t(
                "workspace.message.unexpectedError",
                "An unexpected error occurred"
              ),
            })
          }
        },
      })
    } else {
      console.error("Unsupported agent type for create:", agentType)
      setIsProcessing(false)
      setShowChanged(true)
      onUnsavedChangesUpdate?.(true)
      alertToast({
        title: t("workspace.agent.message.createFailed.title"),
        description: t(
          "workspace.message.unexpectedError",
          "An unexpected error occurred"
        ),
      })
      return
    }
  }

  const onAgentSaved = (agent: Agent, isCreate: boolean) => {
    setIsProcessing(false)
    setShowChanged(false)
    setPendingFormData(null)

    const titleKey = isCreate
      ? "workspace.agent.message.createSuccess.title"
      : "workspace.agent.message.updateSuccess.title"
    const descriptionKey = isCreate
      ? "workspace.agent.message.createSuccess.description"
      : "workspace.agentPlayground.publication.savedDescription"

    successToast({
      title: t(titleKey),
      description: t(descriptionKey, { name: agent.name }),
    })

    // Refresh histories to include the newly created history
    historiesState.loadInitial()

    onAgentChanged?.(agent, isCreate)
  }

  const handleUpdateAgent = (
    agentType: AgentType,
    formData: BasicFormType,
    versionNote: string = ""
  ) => {
    setIsProcessing(true)
    if (agentType === AgentType.Basic) {
      const basicFormData = formData as BasicFormType
      const req = makeUpdateAgentRequest(
        basicFormData.name,
        basicFormData.description,
        basicFormData.systemPrompt,
        basicFormData.unifiedTools,
        basicFormData.attachments,
        basicFormData.llmModel,
        basicFormData.llmModelSettings,
        versionNote,
        basicFormData.suggestedQuestions,
        basicFormData.humanHandoff,
        basicFormData.outputPresentationPolicy,
        basicFormData.responseMode,
        basicFormData.aiTakeoverGreeting
      )
      updateBasicAgent({
        jsonBody: req,
        callbackFn: (resp) => {
          if (!resp || !resp.data) {
            // Handle empty/malformed response - this is an unexpected state
            console.error("Update agent API returned empty or invalid response")
            setIsProcessing(false)
            setShowChanged(true)
            onUnsavedChangesUpdate?.(true)
            alertToast({
              title: t("workspace.message.updateAgentFailed"),
              description: t(
                "workspace.message.unexpectedError",
                "An unexpected error occurred"
              ),
            })
            return
          }
          try {
            const agent = agentResponseToAgent(resp.data)
            onAgentSaved(agent, false)
          } catch (error) {
            console.error("Failed to parse update agent response", error)
            setIsProcessing(false)
            setShowChanged(true)
            onUnsavedChangesUpdate?.(true)
            alertToast({
              title: t("workspace.message.updateAgentFailed"),
              description: t(
                "workspace.message.unexpectedError",
                "An unexpected error occurred"
              ),
            })
          }
        },
      })
    } else {
      console.error("Unsupported agent type for update:", agentType)
      setIsProcessing(false)
      setShowChanged(true)
      onUnsavedChangesUpdate?.(true)
      alertToast({
        title: t("workspace.message.updateAgentFailed"),
        description: t(
          "workspace.message.unexpectedError",
          "An unexpected error occurred"
        ),
      })
      return
    }
  }

  return (
    <div
      className={`${leftPanelBaseWidthClass} ${leftPanelWidthClass} ${leftPanelPaddingClass} py-0 transition-all duration-300 ease-in-out ${leftPanelVisibilityClass} flex flex-col min-h-0`}
    >
      <Card
        className={`h-full flex flex-col min-h-0 gap-2 ${
          !agent && isTemplatePanelOpen
            ? "rounded-none border-0 bg-transparent py-0 shadow-none"
            : "py-4"
        }`}
      >
        <CardHeader hidden={!agent && isTemplatePanelOpen} className="px-4">
          <div className="flex flex-col gap-1">
            <CardTitle className="flex flex-col gap-2">
              <div className="flex items-center gap-1">
                <span className="text-2xl">{getTitle()}</span>
              </div>
              {agent && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="truncate">{agent.id}</span>
                  <TipIconButton
                    tip={t("workspace.agentPlayground.copyAgentId")}
                    button={
                      <div className="transition duration-300 ease-in-out dark:hover:bg-slate-800 hover:bg-slate-100 rounded-sm">
                        <CopyButton
                          content={agent.id}
                          className="h-6 w-6 border-muted-foreground/20"
                        />
                      </div>
                    }
                  />
                </div>
              )}
            </CardTitle>
          </div>
        </CardHeader>

        <CardContent
          className={`p-0 flex-1 min-h-0 overflow-y-auto ${
            !agent && isTemplatePanelOpen
              ? "px-4 py-8 sm:px-6 lg:px-8"
              : "px-4 pb-8"
          }`}
        >
          {agentType && workspace && (
            <div
              className={`pb-2 ${
                !agent && isTemplatePanelOpen ? "mx-auto w-full max-w-5xl" : ""
              }`}
            >
              <ConfigPanel
                key={`${workspace.id}:${agent?.id || "new"}`}
                formId={formId}
                agent={agent}
                agentType={agentType}
                workspace={workspace}
                agents={agents}
                onConfirm={handleApply}
                historiesState={historiesState}
                onDirtyChange={handleDirtyChange}
                onUnsavedChangesChange={onUnsavedChangesUpdate}
                onTemplatePanelOpenChange={setIsTemplatePanelOpen}
                isCreating={isProcessing}
              />
            </div>
          )}
        </CardContent>

        <div
          hidden={!agent && isTemplatePanelOpen}
          className="border-t border-border bg-card px-4 py-3 sticky bottom-0 z-10 mt-auto flex flex-wrap items-center gap-x-4 gap-y-3"
        >
          {agent && !historyUnavailable && currentHistory && (
            <div className="min-w-0 flex-1 basis-48">
              <AgentPublicationStatus
                history={currentHistory}
                latestVersionNumber={
                  agent.latestVersionNumber ??
                  historiesState.histories[0]?.versionNumber
                }
                publishedVersionNumber={publishedVersionNumber}
                hasPublishedVersion={hasPublishedVersion}
                dirty={showChanged}
              />
            </div>
          )}
          <div className="ml-auto flex shrink-0 items-center justify-end gap-2">
            {copilotDraftApplied ? (
              <div
                className="hidden items-center gap-1.5 text-xs text-muted-foreground lg:flex"
                role="status"
              >
                <CheckCircle2Icon className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                <span>
                  {t("workspace.agentPlayground.copilotDraft.appliedPending")}
                </span>
              </div>
            ) : null}
            <div className="flex items-center gap-2">
              <Button
                type="submit"
                size="sm"
                variant="default"
                form={formId}
                disabled={isProcessing || !showChanged}
                className="bg-gray-900 text-white hover:bg-gray-800 disabled:bg-gray-100 disabled:text-gray-400 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400"
              >
                {isProcessing ? (
                  <>
                    <Loader2Icon className="size-4 mr-2 animate-spin" />
                    {t("workspace.agent.message.processing")}
                  </>
                ) : (
                  <>
                    {showChanged && (
                      <BellRingIcon className="size-4 mr-1 text-yellow-500 animate-swing" />
                    )}
                    {saveLabel}
                  </>
                )}
              </Button>
              {/* Snapshot helper (hidden): apply/create button label */}
              <CopilotHiddenField
                fieldKey="toolbar.apply.label"
                value={saveLabel}
              />
            </div>
            {agent && (
              <div className="flex items-center gap-2">
                <CopilotHiddenField fieldKey="agent.meta.id" value={agent.id} />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className={
                    currentHistory?.wasPublished
                      ? "border-red-600 text-red-600 hover:bg-red-50 dark:border-red-400 dark:text-red-400 dark:hover:bg-red-900/20"
                      : "border-emerald-600 text-emerald-600 hover:bg-emerald-50 dark:border-emerald-400 dark:text-emerald-400 dark:hover:bg-emerald-900/20"
                  }
                  onClick={() => setPublishDialogOpen(true)}
                  disabled={isPublicationActionDisabled({
                    isProcessing,
                    historyUnavailable,
                    dirty: showChanged,
                    isPublished: !!currentHistory?.wasPublished,
                  })}
                >
                  {publishLabel}
                </Button>
                {/* Snapshot helper (hidden): publish button label */}
                <CopilotHiddenField
                  fieldKey="toolbar.publish.label"
                  value={publishLabel}
                />
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Publish Dialog */}
      {agent && (
        <AgentHistoryPublishDialog
          open={publishDialogOpen}
          onOpenChange={setPublishDialogOpen}
          agent={agent}
          currentHistory={historiesState.currentHistory || undefined}
          organizationId={workspace.organizationId || ""}
          workspaceId={workspace.id || agent.workspaceId || ""}
          onPublishComplete={() => {
            if (historiesState.currentHistory?.id) {
              historiesState.unpublishAllHistories()
              if (!historiesState.currentHistory.wasPublished) {
                historiesState.updateHistoryPublishStatus(
                  historiesState.currentHistory.id,
                  true
                )
              }
              onPublicationChanged({
                agent,
                versionNumber: historiesState.currentHistory.wasPublished
                  ? undefined
                  : historiesState.currentHistory.versionNumber,
              })
            }
          }}
        />
      )}

      {/* Version Note Dialog */}
      <VersionNoteDialog
        open={showVersionNoteDialog}
        onOpenChange={setShowVersionNoteDialog}
        onConfirm={handleVersionNoteConfirm}
        onCancel={handleVersionNoteCancel}
        isLoading={isProcessing}
      />
    </div>
  )
}

interface PanelDividerToggleProps {
  isLeftPanelOpen: boolean
  isRightPanelOpen: boolean
  showCenterToggle: boolean
  onToggleLeftPanel: () => void
  onToggleRightPanel: () => void
}

const PanelDividerToggle = ({
  isLeftPanelOpen,
  isRightPanelOpen,
  showCenterToggle,
  onToggleLeftPanel,
  onToggleRightPanel,
}: PanelDividerToggleProps) => {
  if (isLeftPanelOpen && isRightPanelOpen) {
    if (!showCenterToggle) {
      return null
    }
    return (
      <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 z-20 flex flex-col gap-2 items-center">
        <Button
          className="bg-card dark:bg-card"
          variant="outline"
          size="icon"
          onClick={onToggleLeftPanel}
          aria-label="Collapse left panel"
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </Button>
        <Button
          className="bg-card dark:bg-card"
          variant="outline"
          size="icon"
          onClick={onToggleRightPanel}
          aria-label="Collapse right panel"
        >
          <ChevronRightIcon className="h-4 w-4" />
        </Button>
      </div>
    )
  }

  if (!isLeftPanelOpen) {
    return (
      <div className="absolute top-1/2 -translate-y-1/2 left-0 -translate-x-full -ml-2 z-20">
        <Button
          className="bg-card dark:bg-card"
          variant="outline"
          size="icon"
          onClick={onToggleLeftPanel}
          aria-label="Expand left panel"
        >
          <ChevronRightIcon className="h-4 w-4" />
        </Button>
      </div>
    )
  }

  if (!isRightPanelOpen) {
    return (
      <div className="absolute top-1/2 -translate-y-1/2 right-0 translate-x-full ml-2 z-20">
        <Button
          className="bg-card dark:bg-card"
          variant="outline"
          size="icon"
          onClick={onToggleRightPanel}
          aria-label="Expand right panel"
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </Button>
      </div>
    )
  }

  return null
}

enum RightPanelState {
  Thread = "thread",
  Lock = "lock",
}

interface RightPanelProps {
  isLeftPanelOpen: boolean
  isRightPanelOpen: boolean
  agent?: Agent
  workspace: Workspace
  state?: RightPanelState
  isAgentProcessing: boolean
  agentHistoryId?: string
  historiesState: ReturnType<typeof useAgentHistories>
  showFirstTestGuidance?: boolean
  showCopilotIntroduction?: boolean
  versionTestGuidance?: VersionTestGuidance | null
  onProcessingChange?: (isProcessing: boolean) => void
  onFirstSuccessfulResponse?: () => void
  onOpenCopilot?: () => void
  onDismissCopilotIntroduction?: () => void
  onVersionTestStarted?: () => void
  onLatestUserQuestionChange?: (question?: string) => void
  onCreateEvalPart?: (
    questionPart: ConversationPartApiResponse,
    answerPart: ConversationPartApiResponse,
    allParts: ConversationPartApiResponse[]
  ) => void
}

const RightPanel = ({
  isLeftPanelOpen,
  isRightPanelOpen,
  agent,
  workspace,
  isAgentProcessing,
  agentHistoryId,
  historiesState,
  showFirstTestGuidance,
  showCopilotIntroduction,
  versionTestGuidance,
  onProcessingChange,
  onFirstSuccessfulResponse,
  onOpenCopilot,
  onDismissCopilotIntroduction,
  onVersionTestStarted,
  onLatestUserQuestionChange,
  onCreateEvalPart,
}: RightPanelProps) => {
  const { t } = useTranslation()
  const threadPanelRef = useRef<ThreadPanelHandle>(null)
  const testHistories = useAgentTestHistories(workspace.id, agent?.id)
  const [mutationPending, setMutationPending] = useState(false)
  const pendingSwitchRef = useRef<
    | {
        run: () => Promise<boolean>
        resolve: (committed: boolean) => void
      }
    | undefined
  >(undefined)
  const [confirmSwitchOpen, setConfirmSwitchOpen] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const rightPanelWidthClass = isLeftPanelOpen ? "md:w-1/2" : "md:w-full"
  const panelState = agent ? RightPanelState.Thread : RightPanelState.Lock
  const isSwitchDisabled =
    panelState === RightPanelState.Lock ||
    isAgentProcessing ||
    mutationPending ||
    Boolean(threadPanelRef.current?.isSwitchBlocked())

  const requestSwitch = useCallback(
    (run: () => Promise<boolean>) => {
      if (isSwitchDisabled || threadPanelRef.current?.isSwitchBlocked()) {
        return Promise.resolve(false)
      }
      if (!threadPanelRef.current?.hasUnsentContent()) return run()

      return new Promise<boolean>((resolve) => {
        pendingSwitchRef.current = { run, resolve }
        setConfirmSwitchOpen(true)
      })
    },
    [isSwitchDisabled]
  )

  const startNewTest = useCallback(
    () =>
      requestSwitch(async () => {
        threadPanelRef.current?.startNewTest()
        testHistories.selectHistory(undefined)
        return true
      }),
    [requestSwitch, testHistories]
  )

  const retryWithNewVersion = useCallback(
    () =>
      requestSwitch(async () => {
        startVersionTest({
          question: versionTestGuidance?.question,
          retryQuestion: (question) =>
            threadPanelRef.current?.retryQuestion(question),
          startNewTest: () => threadPanelRef.current?.startNewTest(),
          selectHistory: testHistories.selectHistory,
          onStarted: onVersionTestStarted,
        })
        return true
      }),
    [
      onVersionTestStarted,
      requestSwitch,
      testHistories,
      versionTestGuidance?.question,
    ]
  )

  const handleSwitchConfirm = useCallback(async () => {
    const pending = pendingSwitchRef.current
    pendingSwitchRef.current = undefined
    setConfirmSwitchOpen(false)
    if (!pending) return
    pending.resolve(await pending.run())
  }, [])

  const handleSwitchCancel = useCallback(() => {
    pendingSwitchRef.current?.resolve(true)
    pendingSwitchRef.current = undefined
    setConfirmSwitchOpen(false)
  }, [])

  return (
    <ChatContextProvider>
      <div
        className={`@container/playground w-full ${rightPanelWidthClass} p-0 absolute right-0 top-0 h-full transition-all duration-300 ease-in-out transform ${
          isRightPanelOpen
            ? "translate-x-0"
            : "translate-x-full md:w-0 opacity-0"
        }`}
      >
        <Card className="h-full p-4 flex flex-col gap-2">
          <CardHeader className="p-0 mb-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <CardTitle className="text-2xl">
                  {t("workspace.liveTest")}
                </CardTitle>
                {agent && (
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <span className="text-sm text-muted-foreground">
                      {agent.name}
                    </span>
                    {agentHistoryId && historiesState.currentHistory && (
                      <>
                        <span className="text-xs text-muted-foreground">
                          {t(
                            "workspace.agentPlayground.publication.testingVersion",
                            {
                              version:
                                historiesState.currentHistory.versionNumber,
                            }
                          )}
                        </span>
                        <span
                          className={`rounded-md px-2 py-1 text-xs font-medium ${historiesState.currentHistory.wasPublished ? "bg-muted text-muted-foreground" : "bg-amber-500/10 text-amber-800 dark:text-amber-300"}`}
                        >
                          {t(
                            `workspace.agentPlayground.publication.${historiesState.currentHistory.wasPublished ? "published" : "unpublished"}`
                          )}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1">
                {agent && (
                  <AgentTestHistorySelector
                    key={`${workspace.id}:${agent.id}`}
                    state={testHistories}
                    workspace={workspace}
                    disabled={isSwitchDisabled}
                    mutationPending={mutationPending}
                    onMutationPendingChange={setMutationPending}
                    onSelect={(history) =>
                      requestSwitch(async () => {
                        setMutationPending(true)
                        try {
                          const committed =
                            (await threadPanelRef.current?.loadHistory(
                              history
                            )) ?? false
                          if (committed) testHistories.selectHistory(history)
                          return committed
                        } finally {
                          setMutationPending(false)
                        }
                      })
                    }
                    onStartNew={startNewTest}
                    onDeletedSelected={() => {
                      threadPanelRef.current?.startNewTest()
                      testHistories.selectHistory(undefined)
                    }}
                  />
                )}
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        className="w-8 h-8"
                        variant="ghost"
                        size="icon"
                        disabled={
                          panelState === RightPanelState.Lock ||
                          isSwitchDisabled
                        }
                        onClick={() => {
                          setIsRefreshing(true)
                          void startNewTest()
                          setTimeout(
                            () => setIsRefreshing(false),
                            REFRESH_ANIMATION_DURATION_MS
                          )
                        }}
                      >
                        <RefreshCw
                          className={`w-4 h-4 transition-transform duration-500 ${
                            isRefreshing ? "rotate-180" : ""
                          }`}
                        />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {t("workspace.agentPlayground.refreshConversation")}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0 h-full relative">
            {agent ? (
              <ThreadPanel
                ref={threadPanelRef}
                workspace={workspace}
                agentHistoryId={agentHistoryId}
                agentHistories={historiesState.histories}
                agent={agent}
                showFirstTestGuidance={showFirstTestGuidance}
                showCopilotIntroduction={showCopilotIntroduction}
                showVersionTestGuidance={Boolean(versionTestGuidance)}
                versionTestQuestion={versionTestGuidance?.question}
                versionTestVersion={versionTestGuidance?.version}
                onProcessingChange={onProcessingChange}
                onFirstSuccessfulResponse={onFirstSuccessfulResponse}
                onOpenCopilot={onOpenCopilot}
                onDismissCopilotIntroduction={onDismissCopilotIntroduction}
                onRetryPreviousQuestion={() => void retryWithNewVersion()}
                onLatestUserQuestionChange={onLatestUserQuestionChange}
                onActiveHistoryChange={(history) => {
                  if (!history) return
                  testHistories.upsertHistory(history)
                  testHistories.selectHistory(history)
                }}
                onHistoryTitleUpdated={(historyId, name) =>
                  testHistories.patchHistory(historyId, {
                    name,
                    updatedAt: new Date().toISOString(),
                  })
                }
                onConversationSettled={() =>
                  void testHistories.refreshFirstPage()
                }
                onCreateEvalPart={onCreateEvalPart}
              />
            ) : (
              <LockPanel />
            )}
          </CardContent>
        </Card>
      </div>
      <AlertDialog open={confirmSwitchOpen} onOpenChange={() => undefined}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("workspace.agentPlayground.history.unsentTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("workspace.agentPlayground.history.unsentDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleSwitchCancel}>
              {t("workspace.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleSwitchConfirm()}>
              {t("workspace.continue")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ChatContextProvider>
  )
}

export default AgentPlaygroundPage
